/* eslint-disable camelcase */
import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import { DemoDataService } from '../demo-data.service';
import { GroupLinkService } from '../group-link.service';
import { TransactionService } from '../transaction.service';
import { OKTA_LOGIN_IN_PROGRESS_SESSION_KEY } from '@core/okta-integration/okta-session-keys';
import {
  ANALYTICS_CANCEL_REASONS,
  ANALYTICS_EVENTS,
  ANALYTICS_JOURNEY_ORIGINS,
  ANALYTICS_MAX_ERROR_MESSAGE_LENGTH,
  ANALYTICS_MAX_PARAMETER_VALUE_LENGTH,
  ANALYTICS_SCREENS,
} from './analytics.events';
import type { IAnalyticsService } from './analytics.interface';
import type {
  AbandonParameters,
  AddShippingInfoParameters,
  AddToCartParameters,
  AdlinkClickParameters,
  AnalyticsItem,
  BeginCheckoutParameters,
  CheckoutBackParameters,
  CheckoutCompletedParameters,
  CheckoutFormInvalidParameters,
  DiscountAppliedParameters,
  ErrorParameters,
  PurchaseParameters,
  RainoutInsuranceToggledParameters,
  RemoveFromCartParameters,
  UpgradeAcquiredParameters,
  UpgradeShownParameters,
  UpsellSelectedParameters,
} from './analytics.types';
import { toOptionalId } from './analytics.utility';

@Injectable({ providedIn: 'root' })
export class AnalyticsService implements IAnalyticsService {
  private static readonly JOURNEY_ORIGIN_KEY = 'gsAnalyticsJourneyOrigin';
  // sessionStorage rather than only the in-memory Set below: AnalyticsService is
  // providedIn:'root', so a full page reload/refresh (summary reached via a resolver refetch,
  // not an in-app navigation) constructs a fresh instance with an empty Set, and the reload
  // would otherwise re-report purchase for the same transaction. sessionStorage survives that
  // reload within the same tab.
  private static readonly REPORTED_PURCHASES_KEY =
    'gsAnalyticsReportedPurchases';

  private readonly router = inject(Router);
  private readonly groupLinkService = inject(GroupLinkService);
  private readonly transactionService = inject(TransactionService);
  private readonly demoDataService = inject(DemoDataService);

  // Set by trackCheckoutBack (header Back button) and consumed once by trackAbandon,
  // keeping checkout_back and checkout_abandoned mutually exclusive without coupling the
  // two call sites.
  private checkoutBackInProgress = false;
  // Abandonment armed by the flow the user is currently in, reported by the pagehide
  // handler below. ngOnDestroy does not run when the tab is closed or hard-reloaded,
  // which is the most common way a checkout is actually abandoned.
  private pendingAbandon: AbandonParameters | null = null;
  private pendingAbandonOwner: string | null = null;
  // Keyed on stage *and* the game, so the pagehide flush and a component's ngOnDestroy
  // cannot double-count the same cart. Cleared on purchase.
  private readonly reportedAbandonments = new Set<string>();
  // form_start marks the first interaction only — every later keystroke would
  // otherwise re-fire it and destroy the step's meaning in a funnel.
  private readonly startedForms = new Set<string>();
  // Completed transaction ids. Also consulted by the transaction_cancelled detector so a
  // navigation away from a *successful* checkout is never mistaken for a cancellation.
  private readonly completedTransactions = new Set<string>();
  // Not cleared on purchase: an id sells once. Same reason as completedTransactions above.
  // In-memory fast path only — see hasReportedPurchase()/rememberReportedPurchase(), which also
  // consult REPORTED_PURCHASES_KEY in sessionStorage so this survives a full page reload.
  private readonly reportedPurchases = new Set<string>();
  // upgrade_acquired dedup: the 3s toast in UpgradesService can re-trigger on further seat
  // changes without the milestone being crossed again.
  private readonly reportedUpgradeAcquisitions = new Set<string>();
  // upgrade_shown dedup, same rationale as above: one "you reached the threshold" per
  // upgrade per journey — repeated seat changes around the threshold must not re-count it.
  private readonly reportedUpgradeShown = new Set<string>();
  // Failed submit attempts on the checkout form. Reset on purchase, so the number means
  // "tries within this purchase" rather than "tries since the tab opened".
  private checkoutFormInvalidAttempts = 0;
  // One "shipping" step per checkout; reset on purchase for repeat buyers.
  private hasReportedShippingInfo = false;
  // The previously resolved screen name, used only to detect the checkout ->
  // seat_selection | events_list backward navigation that fires transaction_cancelled.
  private previousScreen: string | null = null;
  // Best-effort cause for the next transaction_cancelled, set by trackCheckoutBack() and by
  // setPendingCancelReason() from the timer-expiry / patron-retry call sites. Read once and
  // cleared when the cancelling navigation is detected; falls back to 'unknown' otherwise.
  private pendingCancelReason: string | null = null;
  // Fallback transaction id for detectTransactionCancellation(), captured from the
  // /checkout/:id URL itself whenever the checkout screen is entered. Needed because a
  // transaction that failed to load (checkout.component.ts's transaction_not_found path)
  // never populates TransactionService.transaction$, so without this the whole
  // transaction_cancelled event was silently lost on that path.
  private lastCheckoutTransactionId: string | null = null;
  // The journey origin captured at the moment trackAbandon() clears it. The abandon fires
  // from ngOnDestroy *before* the NavigationEnd that detectTransactionCancellation() uses,
  // so without this snapshot the transaction_cancelled trailing the same walk-away would
  // lose its attribution. Overwritten/cleared when the next journey records its own origin.
  private lastJourneyOriginSnapshot: string | null = null;

  public constructor() {
    // Centralised screen tracking — fires page_view on every Angular route change, and is
    // also where transaction_cancelled is detected (see detectTransactionCancellation()).
    // Must be instantiated early (AppComponent) to catch the first navigation.
    this.router.events
      .pipe(filter(navigationEvent => navigationEvent instanceof NavigationEnd))
      .subscribe({
        next: navigationEvent => {
          const path = (navigationEvent as NavigationEnd).urlAfterRedirects;
          this.trackScreenView(this.resolveScreenName(path), path);
        },
        error: (error: unknown) => {
          console.error('Unexpected Router events stream error', error);
        },
      });

    // 'pagehide' rather than 'beforeunload': it also fires when the page enters the
    // back/forward cache, and Safari never fires 'unload' reliably at all.
    globalThis.addEventListener('pagehide', () => {
      // An in-flight Okta redirect (Sign In clicked on checkout) also fires pagehide, but the
      // patron is bounced straight back to the same page — reporting it as checkout_abandoned
      // would over-count every sign-in from a held transaction.
      if (this.isOktaRedirectInFlight()) {
        return;
      }
      if (this.pendingAbandon) {
        this.trackAbandon(this.pendingAbandon);
      }
    });
  }

  private isOktaRedirectInFlight(): boolean {
    try {
      return (
        sessionStorage.getItem(OKTA_LOGIN_IN_PROGRESS_SESSION_KEY) !== null
      );
    } catch {
      return false;
    }
  }

  public trackScreenView(screenName: string, path: string): void {
    // Compare against the *previous* screen before it is overwritten below.
    this.detectTransactionCancellation(screenName);

    if (screenName === ANALYTICS_SCREENS.CHECKOUT) {
      this.lastCheckoutTransactionId = this.parseCheckoutTransactionId(path);
    }

    // Set first, send second. GA4's automatically collected events read page_title from
    // document.title, which this SPA never updates on route change.
    this.setGlobalParameters({
      page_title: screenName,
      page_location: globalThis.location.href,
    });

    this.gtag('event', ANALYTICS_EVENTS.PAGE_VIEW, {
      page_title: screenName,
      page_path: path,
      page_location: globalThis.location.href,
    });

    this.previousScreen = screenName;
  }

  public trackViewItemList(listName: string, items: AnalyticsItem[]): void {
    this.gtag('event', ANALYTICS_EVENTS.VIEW_ITEM_LIST, {
      item_list_name: listName,
      items: this.toGa4Items(items),
    });
  }

  public trackSelectItem(listName: string, item: AnalyticsItem): void {
    this.rememberJourneyOrigin();
    this.gtag('event', ANALYTICS_EVENTS.SELECT_ITEM, {
      item_list_name: listName,
      items: this.toGa4Items([item]),
      // The item IS the game for this event (sale-event.component.ts passes the pvEventId/
      // description as itemId/itemName) — flat, per the file-header doc comment in
      // analytics.events.ts, so a report can filter/group on game_id without unnesting items[].
      game_id: item.itemId,
      game_name: item.itemName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackViewItem(item: AnalyticsItem): void {
    this.gtag('event', ANALYTICS_EVENTS.VIEW_ITEM, {
      items: this.toGa4Items([item]),
      // Same reasoning as trackSelectItem above — the item IS the game here too.
      game_id: item.itemId,
      game_name: item.itemName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAddToCart(parameters: AddToCartParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.ADD_TO_CART, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      seat_count: parameters.seatCount,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackRemoveFromCart(parameters: RemoveFromCartParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.REMOVE_FROM_CART, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      seat_count: parameters.seatCount,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackBeginCheckout(parameters: BeginCheckoutParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
      transaction_id: parameters.transactionId,
      game_id: parameters.gameId,
      currency: parameters.currency,
      value: parameters.value,
      fees: parameters.fees,
      seat_count: parameters.seatCount,
      items: this.toGa4Items(parameters.items),
      ...(parameters.buyerType !== undefined && {
        buyer_type: parameters.buyerType,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackFormStart(formName: string): void {
    if (this.startedForms.has(formName)) {
      return;
    }
    this.startedForms.add(formName);

    const parameters = {
      form_name: formName,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
    this.gtag('event', ANALYTICS_EVENTS.FORM_START, parameters);
    this.gtag('event', ANALYTICS_EVENTS.FORM_START_CHECKOUT, parameters);
  }

  public trackLogin(method: string): void {
    this.gtag('event', ANALYTICS_EVENTS.LOGIN, { method });
  }

  public trackLogout(method: string): void {
    this.gtag('event', ANALYTICS_EVENTS.LOGOUT, { method });
  }

  public trackAddShippingInfo(parameters: AddShippingInfoParameters): void {
    if (this.hasReportedShippingInfo) {
      return;
    }
    this.hasReportedShippingInfo = true;

    this.gtag('event', ANALYTICS_EVENTS.ADD_SHIPPING_INFO, {
      currency: parameters.currency,
      value: parameters.value,
      items: this.toGa4Items(parameters.items),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAddPaymentInfo(
    paymentType: string,
    value: number,
    currency: string,
    items: AnalyticsItem[]
  ): void {
    this.gtag('event', ANALYTICS_EVENTS.ADD_PAYMENT_INFO, {
      payment_type: paymentType,
      currency,
      value,
      items: this.toGa4Items(items),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackUpsellSelected(parameters: UpsellSelectedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.UPSELL_SELECTED, {
      ...this.upsellGa4Parameters(parameters),
    });
  }

  public trackUpsellDeselected(parameters: UpsellSelectedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.UPSELL_DESELECTED, {
      ...this.upsellGa4Parameters(parameters),
    });
  }

  /** Identical shape on both halves of the pair; see UpsellSelectedParameters. */
  private upsellGa4Parameters(
    parameters: UpsellSelectedParameters
  ): Record<string, unknown> {
    return {
      upsell_id: parameters.upsellId,
      upsell_name: parameters.upsellName,
      value: parameters.value,
      game_id: parameters.gameId,
      transaction_id: parameters.transactionId,
      ...(toOptionalId(parameters.previousCartId) !== undefined && {
        previous_cart_id: toOptionalId(parameters.previousCartId),
      }),
      ...(toOptionalId(parameters.newCartId) !== undefined && {
        new_cart_id: toOptionalId(parameters.newCartId),
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
  }

  public trackRainoutInsuranceToggled(
    parameters: RainoutInsuranceToggledParameters
  ): void {
    this.gtag('event', ANALYTICS_EVENTS.RAINOUT_INSURANCE_TOGGLED, {
      action: parameters.action,
      insurance_price: parameters.insurancePrice,
      transaction_id: parameters.transactionId,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackDiscountApplied(parameters: DiscountAppliedParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.DISCOUNT_APPLIED, {
      action: parameters.action ?? 'applied',
      discount_amount: parameters.discountAmount,
      transaction_id: parameters.transactionId,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackCheckoutCompleted(parameters: CheckoutCompletedParameters): void {
    if (this.completedTransactions.has(parameters.transactionId)) {
      return;
    }
    this.completedTransactions.add(parameters.transactionId);

    // The journey origin is deliberately left in place: trackPurchase fires right after this
    // (same success handler) and still needs it.
    this.gtag(
      'event',
      ANALYTICS_EVENTS.CHECKOUT_COMPLETED,
      this.checkoutCompletedGa4Parameters(parameters)
    );
  }

  public trackPurchase(parameters: PurchaseParameters): void {
    if (this.hasReportedPurchase(parameters.transactionId)) {
      return;
    }
    this.rememberReportedPurchase(parameters.transactionId);

    this.gtag('event', ANALYTICS_EVENTS.PURCHASE, {
      ...this.checkoutCompletedGa4Parameters(parameters),
      items: this.toGa4Items(parameters.items),
    });
    // The journey is over: the next one must be attributed, deduplicated and
    // form-tracked from scratch. Repeat buyers and sales reps do several per session.
    this.resetJourneyState();
  }

  public trackUpgradeShown(parameters: UpgradeShownParameters): void {
    if (this.reportedUpgradeShown.has(parameters.upgradeId)) {
      return;
    }
    this.reportedUpgradeShown.add(parameters.upgradeId);

    this.gtag('event', ANALYTICS_EVENTS.UPGRADE_SHOWN, {
      upgrade_id: parameters.upgradeId,
      upgrade_name: parameters.upgradeName,
      goal: parameters.goal,
      min_seats_to_show: parameters.minSeatsToShow,
      seats_in_cart: parameters.seatsInCart,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackUpgradeAcquired(parameters: UpgradeAcquiredParameters): void {
    if (this.reportedUpgradeAcquisitions.has(parameters.upgradeId)) {
      return;
    }
    this.reportedUpgradeAcquisitions.add(parameters.upgradeId);

    this.gtag('event', ANALYTICS_EVENTS.UPGRADE_ACQUIRED, {
      upgrade_id: parameters.upgradeId,
      upgrade_name: parameters.upgradeName,
      goal: parameters.goal,
      seats_in_cart: parameters.seatsInCart,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAdlinkClick(parameters: AdlinkClickParameters): void {
    this.gtag('event', ANALYTICS_EVENTS.ADLINK_CLICK, {
      adlink_id: parameters.adlinkId,
      adlink_name: parameters.adlinkName,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackCheckoutFormInvalid(
    parameters: CheckoutFormInvalidParameters
  ): void {
    this.checkoutFormInvalidAttempts += 1;

    this.gtag('event', ANALYTICS_EVENTS.CHECKOUT_FORM_INVALID, {
      // The count is the true total even when the joined list had to be cut.
      invalid_fields: parameters.invalidFields
        .join(',')
        .slice(0, ANALYTICS_MAX_PARAMETER_VALUE_LENGTH),
      invalid_field_count: parameters.invalidFields.length,
      attempt_number: this.checkoutFormInvalidAttempts,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackError(parameters: ErrorParameters): void {
    // Never let a message-less rejection (e.g. an ApiException constructed from a body with
    // no `message`, aborted fetch) crash the emit — the call sites' `?? fallback` guards are
    // the contract; this is the safety net beneath it.
    const errorMessage = parameters.errorMessage ?? '';

    this.gtag('event', parameters.eventName, {
      error_type: parameters.errorType,
      error_message: errorMessage.slice(0, ANALYTICS_MAX_ERROR_MESSAGE_LENGTH),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public trackAbandon(parameters: AbandonParameters): void {
    // If the header Back button was used, trackCheckoutBack already fired checkout_back.
    // Consume the sentinel and bail — the two events are mutually exclusive.
    if (this.checkoutBackInProgress) {
      this.checkoutBackInProgress = false;
      this.clearPendingAbandon();
      this.resetJourneyMilestones();
      return;
    }
    const abandonmentKey = [
      parameters.stage,
      parameters.gameId ?? '',
      parameters.transactionId ?? '',
    ].join(':');
    if (this.reportedAbandonments.has(abandonmentKey)) {
      return;
    }
    this.reportedAbandonments.add(abandonmentKey);
    this.clearPendingAbandon();

    const eventName =
      parameters.stage === 'cart'
        ? ANALYTICS_EVENTS.CART_ABANDONED
        : ANALYTICS_EVENTS.CHECKOUT_ABANDONED;

    this.gtag('event', eventName, {
      stage: parameters.stage,
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      ...(parameters.transactionId !== undefined && {
        transaction_id: parameters.transactionId,
      }),
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...(parameters.currency !== undefined && {
        currency: parameters.currency,
      }),
      ...(parameters.seatCount !== undefined && {
        seat_count: parameters.seatCount,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });

    // The journey ended here. Snapshot the origin first, so the transaction_cancelled that
    // detectTransactionCancellation() fires on the *following* NavigationEnd (ngOnDestroy of
    // the leaving component runs before it) is still attributed to the journey that ended.
    this.lastJourneyOriginSnapshot = this.readJourneyOrigin();
    this.clearJourneyOrigin();
    // ...and a retry (new form_start, new upgrade_acquired milestone, etc.) must be tracked
    // from scratch rather than staying silently suppressed by the ended journey's state.
    this.resetJourneyMilestones();
  }

  public setPendingAbandon(
    owner: string,
    parameters: AbandonParameters | null
  ): void {
    if (
      this.pendingAbandonOwner !== null &&
      this.pendingAbandonOwner !== owner
    ) {
      return;
    }
    this.pendingAbandon = parameters;
    this.pendingAbandonOwner = parameters === null ? null : owner;
  }

  public isPendingAbandonArmed(owner: string): boolean {
    return this.pendingAbandonOwner === owner;
  }

  public trackCheckoutBack(parameters: CheckoutBackParameters): void {
    // Sentinel consumed by trackAbandon to prevent double-firing when the header Back
    // button is used (checkout_back and checkout_abandoned are mutually exclusive), and by
    // detectTransactionCancellation() to label the resulting transaction_cancelled.
    this.checkoutBackInProgress = true;
    this.pendingCancelReason = ANALYTICS_CANCEL_REASONS.CHECKOUT_BACK;

    this.gtag('event', ANALYTICS_EVENTS.CHECKOUT_BACK, {
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      transaction_id: parameters.transactionId,
      ...(parameters.value !== undefined && { value: parameters.value }),
      ...(parameters.currency !== undefined && {
        currency: parameters.currency,
      }),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  public setPendingCancelReason(reason: string): void {
    this.pendingCancelReason = reason;
  }

  // ── Transaction cancellation ───────────────────────
  /**
   * Fires transaction_cancelled the moment the resolved screen transitions from checkout
   * back to seat_selection or events_list — every current cancel path (header Back, timer
   * expiry, patron-association retry, error-modal dismissal) ends in exactly this
   * navigation, so detecting the *direction* is more robust than instrumenting each call
   * site individually. `reason` is read best-effort from whichever local sentinel is set
   * (trackCheckoutBack / setPendingCancelReason), falling back to 'unknown'.
   */
  private detectTransactionCancellation(newScreen: string): void {
    const isBackwardFromCheckout =
      this.previousScreen === ANALYTICS_SCREENS.CHECKOUT &&
      (newScreen === ANALYTICS_SCREENS.SEAT_SELECTION ||
        newScreen === ANALYTICS_SCREENS.EVENTS_LIST);

    if (!isBackwardFromCheckout) {
      return;
    }

    const transaction = this.transactionService.transaction$.value?.transaction;
    // Falls back to the id captured from the checkout URL (see trackScreenView above) when
    // the transaction never loaded — otherwise a transaction_not_found cancellation produces
    // zero analytics events, since TransactionService.transaction$ is empty on that path.
    const transactionId =
      toOptionalId(transaction?.id) ??
      this.lastCheckoutTransactionId ??
      undefined;
    if (!transactionId) {
      return;
    }
    // A navigation away from an already-completed sale is not a cancellation.
    if (
      this.completedTransactions.has(transactionId) ||
      this.hasReportedPurchase(transactionId)
    ) {
      return;
    }

    const reason = this.pendingCancelReason ?? ANALYTICS_CANCEL_REASONS.UNKNOWN;
    this.pendingCancelReason = null;
    this.lastCheckoutTransactionId = null;

    const gameId = toOptionalId(transaction?.event?.pvEventId);
    const value = transaction?.tdcTransaction?.balance;

    this.gtag('event', ANALYTICS_EVENTS.TRANSACTION_CANCELLED, {
      transaction_id: transactionId,
      reason,
      ...(gameId !== undefined && { game_id: gameId }),
      ...(value !== undefined && { value }),
      seat_count: this.transactionService.getTotalSeats(),
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    });
  }

  // ── Journey origin ─────────────────────────────────────────────────────────
  /**
   * Records the entry point on the first selection of a journey only — later selections
   * belong to the same journey and must not overwrite it. Read from whether a group link
   * is active *at that moment* (a group link maps 1:1 to a sales rep / channel), not from
   * the list name, since GS has only one list. sessionStorage rather than a field, so a
   * mid-flow reload keeps the attribution.
   */
  private rememberJourneyOrigin(): void {
    // A new journey is starting (or continuing): whatever an earlier abandon snapshotted is
    // no longer relevant once the live origin is (re-)set or about to be.
    this.lastJourneyOriginSnapshot = null;
    if (this.readJourneyOrigin()) {
      return;
    }
    const origin = this.groupLinkService.getGroupLink()
      ? ANALYTICS_JOURNEY_ORIGINS.GROUP_LINK
      : ANALYTICS_JOURNEY_ORIGINS.EVENTS_LIST;
    try {
      sessionStorage.setItem(AnalyticsService.JOURNEY_ORIGIN_KEY, origin);
    } catch {
      // Private-mode / disabled storage: attribution is lost, tracking must not break.
    }
  }

  private journeyOriginParameter(): Record<string, string> {
    const origin = this.readJourneyOrigin() ?? this.lastJourneyOriginSnapshot;
    return origin ? { journey_origin: origin } : {};
  }

  private readJourneyOrigin(): string | null {
    try {
      return sessionStorage.getItem(AnalyticsService.JOURNEY_ORIGIN_KEY);
    } catch {
      return null;
    }
  }

  private clearPendingAbandon(): void {
    this.pendingAbandon = null;
    this.pendingAbandonOwner = null;
  }

  private resetJourneyState(): void {
    this.clearJourneyOrigin();
    this.lastJourneyOriginSnapshot = null;
    this.clearPendingAbandon();
    this.reportedAbandonments.clear();
    this.resetJourneyMilestones();
  }

  /**
   * Per-journey milestone dedup, shared by resetJourneyState() (journey completed via
   * trackPurchase) and trackAbandon() (journey ended without completing) — either way, the
   * next journey must be tracked from scratch. Deliberately excludes reportedAbandonments:
   * that set protects against double-counting *this* abandon occurrence (the pagehide flush
   * and a component's ngOnDestroy racing for the same cart), not against a legitimate later
   * occurrence, and trackAbandon() itself decides when to clear it.
   */
  private resetJourneyMilestones(): void {
    this.startedForms.clear();
    this.reportedUpgradeAcquisitions.clear();
    this.reportedUpgradeShown.clear();
    this.checkoutFormInvalidAttempts = 0;
    this.hasReportedShippingInfo = false;
  }

  private clearJourneyOrigin(): void {
    try {
      sessionStorage.removeItem(AnalyticsService.JOURNEY_ORIGIN_KEY);
    } catch {
      // Nothing to clear if storage is unavailable.
    }
  }

  // ── Purchase dedup (survives a page reload — see REPORTED_PURCHASES_KEY) ───────────────
  // Known residual gap: sessionStorage is scoped to the tab. Opening the summary confirmation
  // link in a *new* tab or a later browser session (email link, bookmark, days later) still
  // re-fires purchase for an already-sold transaction, since that tab's storage is empty.
  // Accepted tradeoff for now — GA4's own session-scoped dedup partially masks it in the
  // Key-events revenue view, though not in raw events/explorations/BigQuery. Closing it
  // properly needs a freshness signal (e.g. only fire the summary fallback within N minutes
  // of the transaction's completion timestamp) rather than storage alone.
  private hasReportedPurchase(transactionId: string): boolean {
    return (
      this.reportedPurchases.has(transactionId) ||
      this.readReportedPurchaseIds().includes(transactionId)
    );
  }

  private rememberReportedPurchase(transactionId: string): void {
    this.reportedPurchases.add(transactionId);
    try {
      const ids = this.readReportedPurchaseIds();
      if (!ids.includes(transactionId)) {
        ids.push(transactionId);
        sessionStorage.setItem(
          AnalyticsService.REPORTED_PURCHASES_KEY,
          JSON.stringify(ids)
        );
      }
    } catch {
      // Private-mode / disabled storage: falls back to in-memory-only dedup for this tab.
    }
  }

  private readReportedPurchaseIds(): string[] {
    try {
      const raw = sessionStorage.getItem(
        AnalyticsService.REPORTED_PURCHASES_KEY
      );
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * The real inbound-channel/sales-rep signal (a group link maps to a GroupLinkSalesRepId).
   * Read live off GroupLinkService rather than frozen at first touch: unlike journey_origin
   * (which answers "how did this journey start"), this answers "which channel is active
   * right now", and GroupLinkService already persists it in sessionStorage for the session.
   */
  private groupLinkSlugParameter(): Record<string, string> {
    const slug = toOptionalId(
      this.groupLinkService.getGroupLink() ?? undefined
    );
    return slug !== undefined ? { group_link_slug: slug } : {};
  }

  private checkoutCompletedGa4Parameters(
    parameters: CheckoutCompletedParameters
  ): Record<string, unknown> {
    return {
      transaction_id: parameters.transactionId,
      ...(toOptionalId(parameters.tdcTransactionId) !== undefined && {
        tdc_transaction_id: toOptionalId(parameters.tdcTransactionId),
      }),
      ...(parameters.gameId !== undefined && { game_id: parameters.gameId }),
      item_count: parameters.itemCount,
      seat_count: parameters.seatCount,
      has_upsell: parameters.hasUpsell,
      has_rainout_insurance: parameters.hasRainoutInsurance,
      discount_applied: parameters.discountApplied,
      value: parameters.value,
      fees: parameters.fees,
      currency: parameters.currency,
      ...this.journeyOriginParameter(),
      ...this.groupLinkSlugParameter(),
    };
  }

  private toGa4Items(items: AnalyticsItem[]): Record<string, unknown>[] {
    return items.map(item => ({
      item_id: item.itemId,
      item_name: item.itemName,
      ...(item.price !== undefined && { price: item.price }),
      ...(item.quantity !== undefined && { quantity: item.quantity }),
      ...(item.index !== undefined && { index: item.index }),
      ...(item.itemCategory !== undefined && {
        item_category: item.itemCategory,
      }),
      ...(item.itemListName !== undefined && {
        item_list_name: item.itemListName,
      }),
    }));
  }

  private gtag(
    command: string,
    eventName: string,
    parameters: Record<string, unknown>
  ): void {
    this.callGtag(command, eventName, parameters);
  }

  /**
   * Parameters applied to every event sent afterwards, including the ones GA4 collects
   * on its own. Two arguments, unlike an event call — `gtag('set', { ... })`.
   */
  private setGlobalParameters(parameters: Record<string, unknown>): void {
    this.callGtag('set', parameters);
  }

  private callGtag(...callArguments: unknown[]): void {
    // Same rule AdobeDataLayerService already follows: analytics must be inert in demo mode.
    if (this.demoDataService.isDemoActive()) {
      return;
    }
    const gtagFunction = (
      globalThis as unknown as { gtag?: (...callArguments: unknown[]) => void }
    ).gtag;
    if (typeof gtagFunction !== 'function') {
      return;
    }
    gtagFunction(...callArguments);
  }

  /**
   * A new route needs a segment check here or it reports as 'unknown'. Segment membership
   * (not substring matching) is used deliberately: a group-link slug can never collide with
   * 'events' | 'seat-selection' | 'checkout' | 'summary' (groupLinkGuard's KNOWN_ROUTE_SEGMENTS
   * already forbids it), so this is safe with or without a group-link URL prefix.
   */
  private resolveScreenName(url: string): string {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    if (segments.includes('checkout')) {
      return ANALYTICS_SCREENS.CHECKOUT;
    }
    if (segments.includes('summary')) {
      return ANALYTICS_SCREENS.CHECKOUT_CONFIRMATION;
    }
    if (segments.includes('seat-selection')) {
      return ANALYTICS_SCREENS.SEAT_SELECTION;
    }
    if (segments.includes('events')) {
      return ANALYTICS_SCREENS.EVENTS_LIST;
    }
    return ANALYTICS_SCREENS.UNKNOWN;
  }

  /**
   * The `:id` segment right after `checkout` in the `/checkout/:id` route — used only as the
   * detectTransactionCancellation() fallback above.
   */
  private parseCheckoutTransactionId(path: string): string | null {
    const segments = path.split('?')[0].split('/').filter(Boolean);
    const checkoutIndex = segments.indexOf('checkout');
    return checkoutIndex !== -1 ? (segments[checkoutIndex + 1] ?? null) : null;
  }
}
